/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import { Channel } from '../../core/entities/Channel';
import { MILLISECONDS_IN_A_SECOND } from '../../config';
import { DeviceFeatures } from '../../core/device';
import { SocuDataModelImpl } from '../../core/socu/impls';
import { VideoManager } from '../../core/videoplayer';
import {
    isPlayPauseRestricted,
    onKeyAdvanceVideoPlayer,
    onKeyBackVideoPlayer,
    onKeyChannelDownVideoPlayer,
    onKeyChannelUpVideoPlayer,
    onKeyFastForwardVideoPlayer,
    onKeyLeftVideoPlayer,
    onKeyPauseVideoPlayer,
    onKeyPlayVideoPlayer,
    onKeyReplayVideoPlayer,
    onKeyRewindVideoPlayer,
    onKeyRightVideoPlayer,
    onKeyStopVideoPlayer,
} from '../videoplayer/navigation/videoplayernavigation.reducers';
import { ScreenTaskControllerTypes } from '../../core/navigation/ScreenTaskControllerTypes';
import {
    onKeyBackSubtitleAndCCLanguageOverlay,
    onKeyOkSubtitleAndCCLanguageOverlay,
} from '../videoplayer/overlays/subtitleandcclanguageoverlay/subtitleandcclanguageoverlay.reducers';
import {
    onKeyBackAudioTrackOverlay,
    onKeyOkAudioTrackOverlay,
} from '../videoplayer/overlays/audiotrackoverlay/audiotrackoverlay.reducers';
import {
    onKeyBackFullInfoBanner,
    onKeyChannelDownFullInfoBanner,
    onKeyChannelUpFullInfoBanner,
    onKeyDownFullInfoBanner,
    onKeyLeftFullInfoBanner,
    onKeyOkFullInfoBanner,
    onKeyRightFullInfoBanner,
} from './infobanner/fullinfobannernavigation.reducers';
import {
    onKeyBackTrickplayMenu,
    onKeyChannelDownTrickplayMenu,
    onKeyChannelUpTrickplayMenu,
    onKeyInfoTrickplayMenu,
    onKeyLongLeftTrickplayMenu,
    onKeyLeftTrickplayMenu,
    onKeyOkTrickplayMenu,
    onKeyLongRightTrickplayMenu,
    onKeyRightTrickplayMenu,
    clearLongPressIntervalTrickplayMenu,
} from './infobanner/trickplaymenunavigation.reducers';
import {
    hideFullInfo,
    hideInfoBanners,
    hideStandardInfo,
    hideTrickPlayMenu,
    setActionsStripHighlightBorderActive,
    setEnteringChannelNumberEnabled,
    showFullInfo,
    showStandardInfo,
    showTrickPlayMenu,
    updateFullInfoBannerTimer,
    updateStandardInfoBannerTimer,
    updateTrickPlayMenuTimer,
} from './infobanner/infobanner.reducers';
import {
    onKeyBackStandardInfoBanner,
    onKeyChannelDownStandardInfoBanner,
    onKeyChannelUpStandardInfoBanner,
    onKeyInfoStandardInfoBanner,
    onKeyLeftStandardInfoBanner,
    onKeyOkStandardInfoBanner,
    onKeyRightStandardInfoBanner,
} from './infobanner/standardinfobannernavigation.reducers';
import {
    hideStillThereOsd,
    resetPlaybackParamsVideoplayer,
} from '../videoplayer/videoplayer.reducers';
import { MediaButtonTypes } from './infobanner/mediabuttontypes';
import { PlaybackState, TrickPlayType } from './infobanner/PlaybackTypes';
import {
    onKeyBackErrorOverlay,
    onKeyChanelUpErrorOverlay,
    onKeyChannelDownErrorOverlay,
    onKeyOkErrorOverlay,
} from '../videoplayer/overlays/erroroverlay/erroroverlay.reducers';
import { setScreenTaskController } from '../screens/screens.reducers';
import { NetworkManager } from '../../core/network/NetworkManager';
import {
    setCurrentActiveIndexAction,
    setMediaButtonMouseOverIndex,
} from './infobanner/infobanner.actions';
import {
    onMouseClickRootInteractiveLayerOperations,
    onMouseClickVideoPlayerUiInteractiveLayerOperations,
    onMouseOutVideoPlayerUiOperations,
    onMouseOverVideoPlayerUiOperations,
} from './videoplayeruimouseoperations.reducers';
import { Constants } from '../../core/constants';
import {
    onKeyDownOverlayDialaog,
    onKeyUpOverlayDialog,
    onMouseOutOverlayDialogOperations,
    onMouseOverOverlayDialogOperations,
} from '../overlaydialog/overlaydialog.reducers';
import {
    onKeyBackVodResumeStartOverOverlay,
    onKeyOkVodResumeStartOverOverlayFromLiveTv,
    prepareVodResumeStartOverOverlayFromLiveTv,
} from '../overlay/vodresumestartoveroverlay/vodresumestartoveroverlay.reducers';
import { onKeyOkSocuErrorOverlay } from '../overlay/socuerroroverlay/socuerroroverlay.reducres';

export const swtichToPreviousControllerFromInteractiveLayer =
    () => (dispatch: Function, getState: Function) => {
        const { screenTaskController, previousScreenTaskController } = getState().screens;
        if (screenTaskController === ScreenTaskControllerTypes.LIVE_TV_INTERACTIVE_LAYER_TASKS) {
            dispatch(setScreenTaskController(previousScreenTaskController));
        }
    };

export const onKeyBackVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController, previousScreenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.AUDIO_TRACK_OVERLAY:
            dispatch(onKeyBackAudioTrackOverlay());
            break;

        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(onKeyBackVideoPlayer());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
        case ScreenTaskControllerTypes.LIVE_TV_INTERACTIVE_LAYER_TASKS:
            dispatch(onKeyBackFullInfoBanner());
            break;

        case ScreenTaskControllerTypes.SOCU_ERROR_OVERLAY_TASKS:
            dispatch(onKeyOkSocuErrorOverlay());
            break;

        case ScreenTaskControllerTypes.SUBTITLE_AND_CC_LANGUAGE_OVERLAY:
            dispatch(onKeyBackSubtitleAndCCLanguageOverlay());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(onKeyBackTrickplayMenu());
            break;

        case ScreenTaskControllerTypes.STANDARD_INFO:
            dispatch(onKeyBackStandardInfoBanner());
            break;

        case ScreenTaskControllerTypes.PLAYER_ERROR_OVERLAY:
            dispatch(onKeyBackErrorOverlay());
            dispatch(onKeyBackVideoPlayer());
            break;

        case ScreenTaskControllerTypes.STILL_THERE_OSD_TASKS:
            dispatch(hideStillThereOsd());
            break;

        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onKeyBackVodResumeStartOverOverlay());
            break;

        case ScreenTaskControllerTypes.PREPARING_VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(setScreenTaskController(previousScreenTaskController));
            break;

        default:
            break;
    }
};

export const onKeyChannelDownVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    if (NetworkManager.isNetworkDisconnected()) {
        return;
    }
    const { screenTaskController } = getState().screens;

    // If any new ScreenTask is added, Kindly verify if channel change need to be added or not
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(onKeyChannelDownVideoPlayer());
            dispatch(showStandardInfo());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(onKeyChannelDownVideoPlayer());
            dispatch(onKeyChannelDownFullInfoBanner());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(onKeyChannelDownVideoPlayer());
            dispatch(onKeyChannelDownTrickplayMenu());
            break;

        case ScreenTaskControllerTypes.STANDARD_INFO:
            dispatch(onKeyChannelDownVideoPlayer());
            dispatch(onKeyChannelDownStandardInfoBanner());
            break;

        case ScreenTaskControllerTypes.PLAYER_ERROR_OVERLAY:
            dispatch(onKeyChannelDownErrorOverlay());
            dispatch(onKeyChannelDownVideoPlayer());
            dispatch(showStandardInfo());
            break;

        default:
            break;
    }
};

export const onKeyChannelUpVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    if (NetworkManager.isNetworkDisconnected()) {
        return;
    }
    const { screenTaskController } = getState().screens;

    // If any new ScreenTask is added, Kindly verify if channel change is needed or not
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(onKeyChannelUpVideoPlayer());
            dispatch(showStandardInfo());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(onKeyChannelUpVideoPlayer());
            dispatch(onKeyChannelUpFullInfoBanner());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(onKeyChannelUpVideoPlayer());
            dispatch(onKeyChannelUpTrickplayMenu());
            break;

        case ScreenTaskControllerTypes.STANDARD_INFO:
            dispatch(onKeyChannelUpVideoPlayer());
            dispatch(onKeyChannelUpStandardInfoBanner());
            break;

        case ScreenTaskControllerTypes.PLAYER_ERROR_OVERLAY:
            dispatch(onKeyChanelUpErrorOverlay());
            dispatch(onKeyChannelUpVideoPlayer());
            dispatch(showStandardInfo());
            break;

        default:
            break;
    }
};

export const onKeyDigitVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    if (NetworkManager.isNetworkDisconnected()) {
        return;
    }
    const { screenTaskController } = getState().screens;
    const { isStandardInfoActive, isActionsStripActive, isFullInfoActive } = getState().infoBanner;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.FULL_INFO:
            // In case the info banner is collapsed
            if (!isStandardInfoActive && !isFullInfoActive && isActionsStripActive) {
                dispatch(hideFullInfo());
                dispatch(showStandardInfo());
            }
            dispatch(setEnteringChannelNumberEnabled(true));
            break;
        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
        case ScreenTaskControllerTypes.STANDARD_INFO:
            dispatch(setEnteringChannelNumberEnabled(true));
            break;

        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(showStandardInfo());
            dispatch(setEnteringChannelNumberEnabled(true));
            break;

        default:
            break;
    }
};

export const onKeyDownVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.AUDIO_TRACK_OVERLAY:
        case ScreenTaskControllerTypes.SUBTITLE_AND_CC_LANGUAGE_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onKeyDownOverlayDialaog());
            break;

        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(showFullInfo());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(onKeyDownFullInfoBanner());
            break;

        default:
            break;
    }
};

export const onKeyExitVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { isOverlayDialogActive } = getState().overlayDialog;

    if (isOverlayDialogActive || !SocuDataModelImpl.getInstance().isSocu()) return;

    dispatch(resetPlaybackParamsVideoplayer());
    VideoManager.getInstance().switchToLiveTv();
};

export const onKeyGuideVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
        case ScreenTaskControllerTypes.FULL_INFO:
        case ScreenTaskControllerTypes.SOCU_ERROR_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.STANDARD_INFO:
        case ScreenTaskControllerTypes.PLAYER_ERROR_OVERLAY:
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(onKeyBackTrickplayMenu());
            break;

        case ScreenTaskControllerTypes.AUDIO_TRACK_OVERLAY:
            dispatch(onKeyBackAudioTrackOverlay());
            break;

        case ScreenTaskControllerTypes.SUBTITLE_AND_CC_LANGUAGE_OVERLAY:
            dispatch(onKeyBackSubtitleAndCCLanguageOverlay());
            break;

        case ScreenTaskControllerTypes.STILL_THERE_OSD_TASKS:
            // If audio/subtitle overlay will change to time based hide.
            // This code will still valid.
            dispatch(onKeyBackSubtitleAndCCLanguageOverlay());
            dispatch(onKeyBackAudioTrackOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onKeyBackVodResumeStartOverOverlay());
            break;

        default:
            break;
    }
    dispatch(onKeyBackVideoPlayer());
};

export const onKeyInfoVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(showFullInfo());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(onKeyInfoTrickplayMenu());
            break;

        case ScreenTaskControllerTypes.STANDARD_INFO:
            dispatch(onKeyInfoStandardInfoBanner());
            break;

        default:
            break;
    }
};

export const onKeyLeftVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(onKeyLeftVideoPlayer());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(onKeyLeftFullInfoBanner());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(onKeyLeftTrickplayMenu());
            break;

        case ScreenTaskControllerTypes.STANDARD_INFO:
            dispatch(onKeyLeftStandardInfoBanner());
            break;

        default:
            break;
    }
};

export const onKeyLongLeftVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(onKeyLongLeftTrickplayMenu());
            break;

        default:
            break;
    }
};

export const clearLongPressIntervalVideoPlayerUi =
    () => (dispatch: Function, getState: Function) => {
        const { screenTaskController } = getState().screens;
        switch (screenTaskController) {
            case ScreenTaskControllerTypes.TRICKPLAY_MENU:
                dispatch(clearLongPressIntervalTrickplayMenu());
                break;

            default:
                break;
        }
    };

export const onKeyOkVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.AUDIO_TRACK_OVERLAY:
            dispatch(onKeyOkAudioTrackOverlay());
            break;

        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(showTrickPlayMenu());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(onKeyOkFullInfoBanner());
            break;

        case ScreenTaskControllerTypes.LIVE_TV_INTERACTIVE_LAYER_TASKS:
            dispatch(onMouseClickVideoPlayerUiInteractiveLayerOperations());
            break;

        case ScreenTaskControllerTypes.SUBTITLE_AND_CC_LANGUAGE_OVERLAY:
            dispatch(onKeyOkSubtitleAndCCLanguageOverlay());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(onKeyOkTrickplayMenu());
            break;

        case ScreenTaskControllerTypes.STANDARD_INFO:
            dispatch(onKeyOkStandardInfoBanner());
            break;
        case ScreenTaskControllerTypes.PLAYER_ERROR_OVERLAY:
            dispatch(onKeyOkErrorOverlay());
            dispatch(onKeyBackVideoPlayer());
            break;
        case ScreenTaskControllerTypes.SOCU_ERROR_OVERLAY_TASKS:
            dispatch(onKeyOkSocuErrorOverlay());
            break;
        case ScreenTaskControllerTypes.STILL_THERE_OSD_TASKS:
            dispatch(hideStillThereOsd());
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onKeyOkVodResumeStartOverOverlayFromLiveTv());
            break;
        default:
            break;
    }
};

/**
 * Triggers on press and hold OK/SELECT event and performs the blow use cases:
 * - If any of the overlay is active, do nothing.
 * - If playing a SoCu content then switch to the live tv.
 * - If playing live tv and SoCu content is available then switch to available SoCu content.
 */
export const onLongKeyOkVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { isOverlayDialogActive } = getState().overlayDialog;
    const channel = VideoManager.getInstance().getPlayable()?.channel;

    if (isOverlayDialogActive) return;
    if (channel && !channel.isSubscribed) return;

    // If SOCU content is playing then switch to live tv.
    if (SocuDataModelImpl.getInstance().isSocu()) {
        dispatch(resetPlaybackParamsVideoplayer());
        dispatch(hideInfoBanners());
        VideoManager.getInstance().switchToLiveTv();
        return;
    }

    const previewData = VideoManager.getInstance().getMetadata().offerPreview;
    const isSocuAvailable: boolean =
        DeviceFeatures.isSocuEnabled() && !!previewData?.isAvailableInSocu;
    if (isSocuAvailable) {
        dispatch(prepareVodResumeStartOverOverlayFromLiveTv());
    }
};

export const onKeyPauseVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { playbackState, isPlaying } = getState().videoPlayer;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(updateStandardInfoBannerTimer());
            dispatch(setCurrentActiveIndexAction(MediaButtonTypes.PLAY_PAUSE_BUTTON_INDEX));
            dispatch(updateTrickPlayMenuTimer());

            dispatch(isPlayPauseRestricted(isPlaying ? TrickPlayType.PAUSE : TrickPlayType.PLAY));
            break;

        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
        case ScreenTaskControllerTypes.FULL_INFO:
        case ScreenTaskControllerTypes.STANDARD_INFO:
            if (screenTaskController === ScreenTaskControllerTypes.STANDARD_INFO)
                dispatch(hideStandardInfo());
            else if (screenTaskController === ScreenTaskControllerTypes.FULL_INFO)
                dispatch(hideFullInfo());
            if (playbackState === PlaybackState.PAUSE) {
                dispatch(onKeyPlayVideoPlayer());
            } else if (playbackState === PlaybackState.PLAY) {
                dispatch(showTrickPlayMenu());
                dispatch(onKeyPauseVideoPlayer());
            }
            break;

        default:
            break;
    }
};

export const onKeyPlayVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { playbackState } = getState().videoPlayer;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.STANDARD_INFO:
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            if (playbackState === PlaybackState.PLAY) break;
            dispatch(onKeyPlayVideoPlayer());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            if (playbackState === PlaybackState.PLAY) break;
            dispatch(onKeyPlayVideoPlayer());
            dispatch(hideFullInfo());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(updateStandardInfoBannerTimer());
            dispatch(updateTrickPlayMenuTimer());
            dispatch(onKeyPlayVideoPlayer());
            break;

        default:
            break;
    }
};

export const onKeyPlayPauseVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { playbackState, isPlaying } = getState().videoPlayer;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.STANDARD_INFO:
        case ScreenTaskControllerTypes.FULL_INFO:
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            if (screenTaskController === ScreenTaskControllerTypes.FULL_INFO)
                dispatch(hideFullInfo());
            if (playbackState === PlaybackState.PLAY) {
                dispatch(showTrickPlayMenu());
                dispatch(onKeyPauseVideoPlayer());
            } else if (playbackState === PlaybackState.PAUSE) {
                dispatch(onKeyPlayVideoPlayer());
            }
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(updateStandardInfoBannerTimer());
            dispatch(updateTrickPlayMenuTimer());
            dispatch(setCurrentActiveIndexAction(MediaButtonTypes.PLAY_PAUSE_BUTTON_INDEX));
            dispatch(isPlayPauseRestricted(isPlaying ? TrickPlayType.PAUSE : TrickPlayType.PLAY));
            break;

        default:
            break;
    }
};

export const onKeyStopVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(onKeyStopVideoPlayer());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(onKeyStopVideoPlayer());
            dispatch(hideFullInfo());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(onKeyStopVideoPlayer());
            dispatch(hideTrickPlayMenu());
            break;

        case ScreenTaskControllerTypes.STANDARD_INFO:
            dispatch(onKeyStopVideoPlayer());
            dispatch(hideStandardInfo());
            break;

        default:
            break;
    }
};

export const onKeyFastForwardVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.STANDARD_INFO:
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(showTrickPlayMenu());
            dispatch(onKeyFastForwardVideoPlayer());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(hideFullInfo());
            dispatch(showTrickPlayMenu());
            dispatch(onKeyFastForwardVideoPlayer());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(updateTrickPlayMenuTimer());
            dispatch(updateStandardInfoBannerTimer());
            dispatch(onKeyFastForwardVideoPlayer());
            break;

        default:
            break;
    }
};

export const onKeyRewindVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.STANDARD_INFO:
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(showTrickPlayMenu());
            dispatch(onKeyRewindVideoPlayer());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(hideFullInfo());
            dispatch(showTrickPlayMenu());
            dispatch(onKeyRewindVideoPlayer());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(updateTrickPlayMenuTimer());
            dispatch(updateStandardInfoBannerTimer());
            dispatch(onKeyRewindVideoPlayer());
            break;

        default:
            break;
    }
};

export const onKeyReplayVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.STANDARD_INFO:
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(showTrickPlayMenu());
            dispatch(onKeyReplayVideoPlayer());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(hideFullInfo());
            dispatch(showTrickPlayMenu());
            dispatch(onKeyReplayVideoPlayer());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(updateTrickPlayMenuTimer());
            dispatch(updateStandardInfoBannerTimer());
            dispatch(onKeyReplayVideoPlayer());
            break;

        default:
            break;
    }
};

export const onKeyAdvanceVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.STANDARD_INFO:
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(showTrickPlayMenu());
            dispatch(onKeyAdvanceVideoPlayer());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(hideFullInfo());
            dispatch(showTrickPlayMenu());
            dispatch(onKeyAdvanceVideoPlayer());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(updateTrickPlayMenuTimer());
            dispatch(updateStandardInfoBannerTimer());
            dispatch(onKeyAdvanceVideoPlayer());
            break;

        default:
            break;
    }
};

export const onKeyRightVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(onKeyRightVideoPlayer());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(onKeyRightFullInfoBanner());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(onKeyRightTrickplayMenu());
            break;

        case ScreenTaskControllerTypes.STANDARD_INFO:
            dispatch(onKeyRightStandardInfoBanner());
            break;

        default:
            break;
    }
};

export const onKeyLongRightVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(onKeyLongRightTrickplayMenu());
            break;

        default:
            break;
    }
};

export const onKeyTivoVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.AUDIO_TRACK_OVERLAY:
            break;

        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            break;

        case ScreenTaskControllerTypes.SUBTITLE_AND_CC_LANGUAGE_OVERLAY:
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            break;

        default:
            break;
    }
};

export const onKeyUpVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.AUDIO_TRACK_OVERLAY:
        case ScreenTaskControllerTypes.SUBTITLE_AND_CC_LANGUAGE_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onKeyUpOverlayDialog());
            break;

        default:
            break;
    }
};

export const onMouseClickVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { inputFactory } = getState();
    const { value } = inputFactory;

    if (value === Constants.ROOT_INTERACTIVE_LAYER || value === Constants.DIMMER) {
        dispatch(onMouseClickRootInteractiveLayerOperations());
        return;
    }
    dispatch(onKeyOkVideoPlayerUi());
};

export const onMouseOverVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    if (value === Constants.ROOT_INTERACTIVE_LAYER) return;
    const screenTaskControllerVal = getState().screens.screenTaskController;
    if (screenTaskControllerVal === ScreenTaskControllerTypes.FULL_INFO) {
        dispatch(onMouseOverVideoPlayerUiOperations());
        dispatch(updateFullInfoBannerTimer());
    }
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.AUDIO_TRACK_OVERLAY:
        case ScreenTaskControllerTypes.SOCU_ERROR_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SUBTITLE_AND_CC_LANGUAGE_OVERLAY:
        case ScreenTaskControllerTypes.PLAYER_ERROR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onMouseOverOverlayDialogOperations());
            break;
        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(setActionsStripHighlightBorderActive(true));
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(setMediaButtonMouseOverIndex(Number(value)));
            dispatch(updateTrickPlayMenuTimer());
            dispatch(updateStandardInfoBannerTimer());
            break;
        default:
    }
};

export const onMouseOutVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const screenTaskControllerVal = getState().screens.screenTaskController;
    if (screenTaskControllerVal === ScreenTaskControllerTypes.LIVE_TV_INTERACTIVE_LAYER_TASKS)
        dispatch(onMouseOutVideoPlayerUiOperations());
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.AUDIO_TRACK_OVERLAY:
        case ScreenTaskControllerTypes.SOCU_ERROR_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SUBTITLE_AND_CC_LANGUAGE_OVERLAY:
        case ScreenTaskControllerTypes.PLAYER_ERROR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onMouseOutOverlayDialogOperations());
            break;
        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(setActionsStripHighlightBorderActive(false));
            break;
        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(setMediaButtonMouseOverIndex(-1));
            break;
        default:
    }
};
